// src/pages/ProofreadingAssignments.tsx
import React from "react";
import { Link } from "react-router-dom";
import { FaInstagram } from "react-icons/fa";

/**
 * ProofreadingAssignments (Blog 8)
 * SEO meta (example): "Master proofreading and editing assignments with expert tips, NLP tools, and step-by-step strategies for flawless academic writing."
 */

export default function ProofreadingAssignments(): JSX.Element {
  return (
    <section className="relative bg-[#02042b] text-white mt-12">
      <div
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          background:
            "radial-gradient(900px 400px at 10% -10%, rgba(99,102,241,.12), transparent 60%), radial-gradient(800px 360px at 120% 0%, rgba(236,72,153,.08), transparent 55%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <header className="text-center max-w-3xl mx-auto mb-10">
          <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight">
            The Ultimate Guide to Proofreading & Editing Your Assignments
          </h1>
          <p className="mt-3 text-gray-300">
            Master step-by-step editing strategies, NLP-powered tools, and practical proofreading techniques to submit clear,
            polished, and plagiarism-free assignments.
          </p>
       
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 bg-white/5 backdrop-blur rounded-xl border border-white/10 p-5">
              <h2 className="text-lg font-semibold mb-3">Table of Contents</h2>
              <nav className="text-sm text-gray-300 space-y-2">
                <a href="#intro" className="block hover:text-white">Introduction</a>
                <a href="#why" className="block hover:text-white">Why It Matters</a>
                <a href="#plan" className="block hover:text-white">Step 1: Plan</a>
                <a href="#structure" className="block hover:text-white">Step 2: Structural Editing</a>
                <a href="#style" className="block hover:text-white">Step 3: Language & Style</a>
                <a href="#proofread" className="block hover:text-white">Step 4: Proofreading</a>
                <a href="#mistakes" className="block hover:text-white">Step 5: Common Mistakes</a>
                <a href="#tools" className="block hover:text-white">Step 6: Tools & Resources</a>
                <a href="#examples" className="block hover:text-white">Step 7: Examples & Checklists</a>
                <a href="#conclusion" className="block hover:text-white">Conclusion</a>
                <a href="#help" className="block hover:text-white">How We Help</a>
                <a href="#related" className="block hover:text-white">Also read</a>
                <a href="#faqs" className="block hover:text-white">FAQs</a>
                <a href="#ack" className="block hover:text-white">Acknowledgement</a>
              </nav>
            </div>
          </aside>

          <article className="lg:col-span-8 space-y-10">
            <section id="intro" className="bg-white/5 rounded-xl border border-white/10 p-6" style={{ scrollMarginTop: "7rem" }}>
              <h2 className="text-2xl font-bold mb-3">Introduction</h2>
              <p className="text-gray-200">
                Proofreading and editing assignments turns good drafts into great submissions. Careful editing improves clarity,
                fixes grammar, ensures consistent formatting, and preserves your academic voice — all of which improve grades and credibility.
              </p>

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <div className="bg-[#071427] p-4 rounded">
                  <h3 className="text-sm font-semibold text-gray-200">Key takeaways</h3>
                  <ul className="text-sm text-gray-300 list-disc pl-5 mt-2 space-y-1">
                    <li>Step-by-step editing strategies for structure and clarity.</li>
                    <li>Proofreading techniques to catch grammar, punctuation, and formatting issues.</li>
                    <li>NLP tools to improve coherence without killing your voice.</li>
                  </ul>
                </div>

                <div className="bg-[#071427] p-4 rounded">
                  <h3 className="text-sm font-semibold text-gray-200">Quick tip</h3>
                  <p className="text-sm text-gray-300">Separate editing passes: structure → language → proofreading. One focus per pass catches more than multitasking.</p>
                </div>
              </div>
            </section>

            <section id="why" className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Why Proofreading & Editing Are Crucial</h2>
              <p className="text-gray-200">
                Editing improves argument flow and evidence; proofreading is the final polish. Together they stop small errors from undermining
                strong ideas and demonstrate professionalism through consistent formatting and accurate citations.
              </p>
            </section>

            <section id="plan" className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Step 1: Planning Your Editing Process</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li><strong>Read through once</strong> to understand overall flow.</li>
                <li><strong>Highlight weak areas</strong> that need more evidence or clearer explanation.</li>
                <li><strong>Set goals</strong> for each pass (structure, clarity, grammar).</li>
                <li><strong>Use NLP insights</strong> — tokenization and POS tagging can flag repetitive patterns while preserving voice.</li>
              </ul>
            </section>

            <section id="structure" className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Step 2: Structural & Content Editing</h2>
              <p className="text-gray-200">Focus on readability and logical flow:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Ensure each paragraph has a clear topic sentence and takes the reader forward.</li>
                <li>Remove weak claims or support them with credible sources.</li>
                <li>Match content to the assignment rubric (word count, headings, required sections).</li>
                <li>Peer feedback is invaluable — fresh eyes spot blind spots.</li>
              </ul>
            </section>

            <section id="style" className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Step 3: Language & Style Editing</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Simplify complex sentences; prefer clarity over flourish.</li>
                <li>Use active voice where appropriate to strengthen arguments.</li>
                <li>Maintain tense and terminology consistency throughout.</li>
                <li>Apply NER checks for correct names, dates, and institutions.</li>
              </ul>
            </section>

            <section id="proofread" className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Step 4: Proofreading for Perfection</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Read aloud to find awkward phrasing.</li>
                <li>Print a copy — printed text often reveals errors on-screen missed earlier.</li>
                <li>Take breaks between passes; a rested eye catches more.</li>
                <li>Use contextual grammar tools (not just spellcheck) to spot subtle errors.</li>
              </ul>
            </section>

            <section id="mistakes" className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Step 5: Common Mistakes to Avoid</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Avoid over-reliance on AI — tools assist but don’t replace subject judgement.</li>
                <li>Don’t skip multiple reviews — one pass is rarely enough.</li>
                <li>Pay attention to formatting rules — incorrect citations can cost marks.</li>
                <li>Incorporate peer feedback — it’s often the fastest way to improve clarity.</li>
              </ul>
            </section>

            <section id="tools" className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Step 6: Recommended Tools & Resources</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li><strong>Grammarly:</strong> grammar/style suggestions and tone checks.</li>
                <li><strong>Hemingway Editor:</strong> readability and sentence complexity.</li>
                <li><strong>AI Assistants:</strong> use for brainstorming and restructure — always rewrite in your voice.</li>
                <li><strong>Plagiarism & Originality:</strong> check with Turnitin or other reliable services.</li>
              </ul>
            </section>

            <section id="examples" className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Step 7: Examples, Checklists & Real-Life Tips</h2>
              <p className="text-gray-200">
                Use before-and-after examples to learn how editing improves clarity. Keep a proofreading checklist that includes
                grammar, citations, headings, and final file naming.
              </p>
              <p className="text-gray-200 mt-3">
                <Link to="/blog/assignment-writing-checklist" className="text-indigo-400 font-semibold hover:text-indigo-300 underline">
                  Also read: Ultimate Assignment Writing Checklist for College Students (2025 Edition)
                </Link>
              </p>
            </section>

            <section id="conclusion" className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Conclusion</h2>
              <p className="text-gray-200">
                Proofreading and editing reflect attention to detail and academic integrity. Combine a multi-pass approach with human review
                and smart tools to produce polished, credible submissions. Start with a plan, edit in passes, and proofread patiently.
              </p>
            </section>

            <section id="help" className="bg-gradient-to-r from-rose-500/20 to-indigo-500/20 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-2">How We Help</h2>
              <p className="text-gray-200">
                Infinite Solution provides expert proofreading, structural editing, and NLP-backed checks for essays, reports, dissertations,
                and theses. We keep your voice, ensure originality, and polish your work to meet university standards.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <Link to="/Contactus" className="inline-flex items-center px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 font-semibold">
                  Get a Free Consultation
                </Link>
                <a href="https://wa.me/917980085981" className="inline-flex items-center px-4 py-2 rounded-lg border border-white/20 hover:bg-white/10">
                  Talk to a Specialist
                </a>
              </div>
            </section>

            <section id="faqs" className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">FAQs</h2>
              <ol className="list-decimal pl-6 space-y-3 text-gray-200">
                <li>
                  <p className="font-semibold">How many editing passes do I need?</p>
                  <p>At least three: structure/content, language/style, and final proofreading.</p>
                </li>
                <li>
                  <p className="font-semibold">Can AI do my proofreading?</p>
                  <p>AI helps but human review is essential for nuance, argument strength, and academic judgement.</p>
                </li>
                <li>
                  <p className="font-semibold">What tools ensure originality?</p>
                  <p>Turnitin and similar plagiarism checkers, plus manual citation checks, are best combined.</p>
                </li>
              </ol>
            </section>

            <section id="ack" className="bg-gradient-to-r from-indigo-900/30 via-purple-900/20 to-pink-900/10 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl sm:text-3xl font-extrabold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-indigo-300 pb-2">
                Acknowledgement
              </h2>
              <p className="text-gray-200">
                This guide was researched and drafted using modern tooling and expert review. Content polished by{" "}
                <a href="https://www.infinitesolutionedu.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 font-semibold underline">
                  Infinite Solution
                </a>.
              </p>

              <p className="text-gray-200 mt-3 inline-flex items-center gap-2">
                <FaInstagram /> <a href="https://www.instagram.com/abhishekshawofficial_?igsh=Ynh2OXl6bm4xN3Z4" target="_blank" rel="noopener noreferrer" className="text-pink-400 underline">@abhishekshaw</a>
              </p>
            </section>
          </article>
        </div>
      </div>
    </section>
  );
}
